import { useToast, Box, Flex, Text, CloseButton, Icon } from '@chakra-ui/react';
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';

const statusConfig = {
  success: {
    icon: FaCheckCircle,
    bg: 'linear-gradient(135deg, rgba(34, 197, 94, 0.95) 0%, rgba(22, 163, 74, 0.95) 100%)',
    borderColor: 'rgba(34, 197, 94, 0.5)',
    iconColor: '#86efac',
    glowColor: 'rgba(34, 197, 94, 0.4)',
  },
  error: {
    icon: FaExclamationCircle,
    bg: 'linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 0.95) 100%)',
    borderColor: 'rgba(239, 68, 68, 0.5)',
    iconColor: '#fca5a5',
    glowColor: 'rgba(239, 68, 68, 0.4)',
  },
  warning: {
    icon: FaExclamationTriangle,
    bg: 'linear-gradient(135deg, rgba(245, 158, 11, 0.95) 0%, rgba(217, 119, 6, 0.95) 100%)',
    borderColor: 'rgba(245, 158, 11, 0.5)',
    iconColor: '#fde68a',
    glowColor: 'rgba(245, 158, 11, 0.4)',
  },
  info: {
    icon: FaInfoCircle,
    bg: 'linear-gradient(135deg, rgba(139, 92, 246, 0.95) 0%, rgba(109, 40, 217, 0.95) 100%)',
    borderColor: 'rgba(139, 92, 246, 0.5)',
    iconColor: '#c4b5fd',
    glowColor: 'rgba(139, 92, 246, 0.4)',
  },
};

export const useCustomToast = () => {
  const toast = useToast();

  const showToast = ({
    title,
    description,
    status = 'info',
    duration = 5000,
    isClosable = true,
    position = 'top-right',
  }) => {
    const config = statusConfig[status] || statusConfig.info;

    return toast({
      position,
      duration,
      isClosable,
      render: ({ onClose }) => (
        <Box
          bg={config.bg}
          backdropFilter="blur(20px)"
          border="1px solid"
          borderColor={config.borderColor}
          borderRadius="16px"
          boxShadow={`0 10px 40px ${config.glowColor}, 0 0 0 1px rgba(255, 255, 255, 0.1) inset`}
          p="16px"
          minW="320px"
          maxW="400px"
          position="relative"
          overflow="hidden"
          animation="slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1)"
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            bg: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
          }}
        >
          <Flex align="flex-start" gap="12px">
            {/* Icon */}
            <Flex
              w="40px"
              h="40px"
              bg="rgba(255, 255, 255, 0.15)"
              borderRadius="12px"
              align="center"
              justify="center"
              flexShrink={0}
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
            >
              <Icon as={config.icon} color={config.iconColor} boxSize={5} />
            </Flex>

            {/* Content */}
            <Flex direction="column" flex="1" minW="0">
              <Text
                color="white"
                fontWeight="700"
                fontSize="md"
                lineHeight="1.3"
                mb={description ? "4px" : "0"}
                textShadow="0 1px 2px rgba(0, 0, 0, 0.2)"
              >
                {title}
              </Text>
              {description && (
                <Text
                  color="rgba(255, 255, 255, 0.85)"
                  fontSize="sm"
                  lineHeight="1.5"
                >
                  {description}
                </Text>
              )}
            </Flex>

            {/* Close button */}
            {isClosable && (
              <CloseButton
                size="sm"
                color="rgba(255, 255, 255, 0.7)"
                _hover={{
                  color: 'white',
                  bg: 'rgba(255, 255, 255, 0.15)',
                }}
                onClick={onClose}
                borderRadius="8px"
              />
            )}
          </Flex>

          {/* Progress bar animation */}
          <Box
            position="absolute"
            bottom="0"
            left="0"
            height="3px"
            bg="rgba(255, 255, 255, 0.3)"
            borderRadius="0 0 16px 16px"
            animation={`shrinkWidth ${duration}ms linear forwards`}
            sx={{
              '@keyframes shrinkWidth': {
                from: { width: '100%' },
                to: { width: '0%' },
              },
            }}
          />
        </Box>
      ),
    });
  };

  // Raccourcis pratiques
  const success = (title, description) => showToast({ title, description, status: 'success' });
  const error = (title, description) => showToast({ title, description, status: 'error' });
  const warning = (title, description) => showToast({ title, description, status: 'warning' });
  const info = (title, description) => showToast({ title, description, status: 'info' });

  return {
    showToast,
    success,
    error,
    warning,
    info,
    // Garde aussi l'accès au toast original si besoin
    toast,
  };
};

export default useCustomToast;
